import React, { useState } from "react";

const gamesList = [
  {
    name: "Food Quiz",
    description:
      "Test your nutrition knowledge with fun multiple-choice questions!",
  },
  {
    name: "Meal Builder Challenge",
    description:
      "Drag and drop foods to build a balanced meal and get instant feedback.",
  },
  {
    name: "Daily Nutrition Streaks",
    description: "Log healthy choices daily and earn streaks and badges.",
  },
  {
    name: "Guess the Calories",
    description:
      "Guess the calorie count of foods and learn portion control tips.",
  },
  {
    name: "Nutrition Bingo",
    description:
      "Complete healthy actions to fill your bingo card and win rewards!",
  },
];

const GameSection = () => {
  const [selectedGame, setSelectedGame] = useState(null);

  return (
    <div className="p-6 md:p-10 bg-gradient-to-br from-yellow-100 via-green-50 to-blue-100 rounded-3xl shadow-2xl mt-6 border-2 border-blue-200">
      <h2 className="text-4xl font-extrabold mb-8 text-blue-700 flex items-center gap-3 drop-shadow-lg">
        🎮 Diet Games
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {gamesList.map((game) => (
          <button
            key={game.name}
            className={`p-6 rounded-2xl shadow-xl bg-white hover:bg-blue-50 border-2 border-blue-200 text-left transition-all duration-300 ${
              selectedGame === game.name ? "scale-105 bg-blue-100" : ""
            }`}
            onClick={() => setSelectedGame(game.name)}
          >
            <h3 className="text-2xl font-bold text-green-700 mb-2">
              {game.name}
            </h3>
            <p className="text-gray-700 text-lg">{game.description}</p>
          </button>
        ))}
      </div>
      {selectedGame && (
        <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-green-200 mt-4">
          <h3 className="text-2xl font-bold text-blue-700 mb-4">
            {selectedGame}
          </h3>
          <div className="text-gray-700 text-lg mb-4">
            {selectedGame === "Food Quiz" && <FoodQuiz />}
            {selectedGame === "Meal Builder Challenge" && (
              <MealBuilderChallenge />
            )}
            {selectedGame === "Daily Nutrition Streaks" && (
              <DailyNutritionStreaks />
            )}
            {selectedGame === "Guess the Calories" && <GuessTheCalories />}
            {selectedGame === "Nutrition Bingo" && <NutritionBingo />}
          </div>
        </div>
      )}
    </div>
  );
};

// --- Game Implementations ---

function shuffle(array) {
  let arr = array.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function FoodQuiz() {
  const allQuestions = [
    {
      q: "Which vitamin is abundant in oranges?",
      options: ["Vitamin A", "Vitamin C", "Vitamin D", "Vitamin B12"],
      answer: 1,
    },
    {
      q: "What is a good source of protein?",
      options: ["Chicken", "Apple", "Rice", "Broccoli"],
      answer: 0,
    },
    {
      q: "Which food is highest in fiber?",
      options: ["White bread", "Banana", "Lentils", "Cheese"],
      answer: 2,
    },
    {
      q: "Which mineral is important for bone health?",
      options: ["Iron", "Calcium", "Potassium", "Zinc"],
      answer: 1,
    },
    {
      q: "Which is a healthy fat?",
      options: ["Butter", "Olive Oil", "Lard", "Margarine"],
      answer: 1,
    },
    {
      q: "Which food is best for hydration?",
      options: ["Watermelon", "Bread", "Cheese", "Chicken"],
      answer: 0,
    },
  ];
  const [questions, setQuestions] = useState(() =>
    shuffle(allQuestions).slice(0, 3)
  );
  const [step, setStep] = useState(0);
  const [score, setScore] = useState(0);
  const [selected, setSelected] = useState(null);
  const [finished, setFinished] = useState(false);

  const handleAnswer = (idx) => {
    setSelected(idx);
    if (idx === questions[step].answer) setScore(score + 1);
    setTimeout(() => {
      if (step < questions.length - 1) {
        setStep(step + 1);
        setSelected(null);
      } else {
        setFinished(true);
      }
    }, 700);
  };

  const handleRestart = () => {
    setQuestions(shuffle(allQuestions).slice(0, 3));
    setStep(0);
    setScore(0);
    setFinished(false);
    setSelected(null);
  };

  if (finished)
    return (
      <div>
        <p className="mb-4">
          Quiz finished! Your score:{" "}
          <span className="font-bold text-green-600">
            {score}/{questions.length}
          </span>
        </p>
        <button
          className="bg-blue-500 text-white px-4 py-2 rounded"
          onClick={handleRestart}
        >
          Restart
        </button>
      </div>
    );

  return (
    <div>
      <p className="mb-4 font-semibold">{questions[step].q}</p>
      <div className="grid grid-cols-2 gap-3">
        {questions[step].options.map((opt, idx) => (
          <button
            key={opt}
            className={`px-4 py-2 rounded border ${
              selected === idx
                ? idx === questions[step].answer
                  ? "bg-green-200 border-green-500"
                  : "bg-red-200 border-red-500"
                : "bg-white border-blue-200"
            }`}
            disabled={selected !== null}
            onClick={() => handleAnswer(idx)}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

function MealBuilderChallenge() {
  const allFoods = [
    { name: "Chicken Breast", type: "Protein" },
    { name: "Brown Rice", type: "Carb" },
    { name: "Broccoli", type: "Veggie" },
    { name: "Olive Oil", type: "Fat" },
    { name: "Apple", type: "Fruit" },
    { name: "Salmon", type: "Protein" },
    { name: "Quinoa", type: "Carb" },
    { name: "Spinach", type: "Veggie" },
    { name: "Avocado", type: "Fat" },
    { name: "Orange", type: "Fruit" },
  ];
  const [foods, setFoods] = useState(() => shuffle(allFoods).slice(0, 5));
  const [selected, setSelected] = useState([]);
  const [feedback, setFeedback] = useState("");

  const handleSelect = (food) => {
    if (!selected.includes(food.name)) setSelected([...selected, food.name]);
  };
  const handleSubmit = () => {
    const types = foods
      .filter((f) => selected.includes(f.name))
      .map((f) => f.type);
    const required = ["Protein", "Carb", "Veggie", "Fat", "Fruit"];
    const missing = required.filter((t) => !types.includes(t));
    if (missing.length === 0)
      setFeedback("Great job! You built a balanced meal.");
    else setFeedback("Missing: " + missing.join(", "));
  };
  const handleReset = () => {
    setSelected([]);
    setFeedback("");
    setFoods(shuffle(allFoods).slice(0, 5));
  };

  return (
    <div>
      <p className="mb-2">Select foods to build a balanced meal:</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {foods.map((food) => (
          <button
            key={food.name}
            className={`px-3 py-2 rounded border ${
              selected.includes(food.name)
                ? "bg-green-200 border-green-500"
                : "bg-white border-blue-200"
            }`}
            onClick={() => handleSelect(food)}
            disabled={selected.includes(food.name)}
          >
            {food.name}
          </button>
        ))}
      </div>
      <button
        className="bg-blue-500 text-white px-4 py-2 rounded mr-2"
        onClick={handleSubmit}
      >
        Check Meal
      </button>
      <button className="bg-gray-300 px-4 py-2 rounded" onClick={handleReset}>
        Reset
      </button>
      {feedback && <p className="mt-3 font-semibold">{feedback}</p>}
    </div>
  );
}

function DailyNutritionStreaks() {
  const [days, setDays] = useState(() => shuffle(Array(7).fill(false)));
  const [streak, setStreak] = useState(0);
  const handleLog = (idx) => {
    const newDays = [...days];
    newDays[idx] = true;
    setDays(newDays);
    setStreak(newDays.filter(Boolean).length);
  };
  const handleReset = () => {
    setDays(shuffle(Array(7).fill(false)));
    setStreak(0);
  };
  return (
    <div>
      <p className="mb-2">Log a healthy choice for each day:</p>
      <div className="flex gap-2 mb-4">
        {days.map((done, idx) => (
          <button
            key={idx}
            className={`w-10 h-10 rounded-full border text-lg font-bold ${
              done
                ? "bg-green-400 border-green-600 text-white"
                : "bg-white border-blue-200 text-blue-700"
            }`}
            onClick={() => handleLog(idx)}
            disabled={done}
          >
            {idx + 1}
          </button>
        ))}
      </div>
      <p className="mb-2">
        Current streak:{" "}
        <span className="font-bold text-green-600">{streak}</span>
      </p>
      {streak === 7 && (
        <p className="font-semibold text-yellow-600">
          🏆 You earned a 7-day streak badge!
        </p>
      )}
      <button
        className="bg-gray-300 px-4 py-2 rounded mt-2"
        onClick={handleReset}
      >
        Reset
      </button>
    </div>
  );
}

function GuessTheCalories() {
  const allFoods = [
    { name: "Banana", calories: 105 },
    { name: "Egg (boiled)", calories: 78 },
    { name: "Slice of bread", calories: 70 },
    { name: "Almonds (10)", calories: 70 },
    { name: "Apple", calories: 95 },
    { name: "Carrot", calories: 25 },
    { name: "Yogurt (cup)", calories: 150 },
    { name: "Chicken leg", calories: 180 },
  ];
  const [foods, setFoods] = useState(() => shuffle(allFoods).slice(0, 4));
  const [step, setStep] = useState(0);
  const [input, setInput] = useState("");
  const [feedback, setFeedback] = useState("");
  const [score, setScore] = useState(0);

  const handleGuess = () => {
    const guess = parseInt(input);
    if (isNaN(guess)) return setFeedback("Enter a number!");
    const diff = Math.abs(guess - foods[step].calories);
    if (diff <= 15) {
      setFeedback("Correct! Actual: " + foods[step].calories);
      setScore(score + 1);
    } else {
      setFeedback("Not quite! Actual: " + foods[step].calories);
    }
    setTimeout(() => {
      if (step < foods.length - 1) {
        setStep(step + 1);
        setInput("");
        setFeedback("");
      } else {
        setFeedback(
          "Game finished! Score: " +
            (score + (diff <= 15 ? 1 : 0)) +
            "/" +
            foods.length
        );
      }
    }, 1200);
  };
  const handleReset = () => {
    setStep(0);
    setInput("");
    setFeedback("");
    setScore(0);
    setFoods(shuffle(allFoods).slice(0, 4));
  };

  return (
    <div>
      <p className="mb-2">
        Guess the calories in:{" "}
        <span className="font-bold">{foods[step].name}</span>
      </p>
      <input
        type="number"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        className="border px-3 py-2 rounded mr-2"
        placeholder="Calories"
        disabled={!!feedback && feedback.startsWith("Game finished")}
      />
      <button
        className="bg-blue-500 text-white px-4 py-2 rounded"
        onClick={handleGuess}
        disabled={!!feedback && feedback.startsWith("Game finished")}
      >
        Guess
      </button>
      <button
        className="bg-gray-300 px-4 py-2 rounded ml-2"
        onClick={handleReset}
      >
        Reset
      </button>
      {feedback && <p className="mt-3 font-semibold">{feedback}</p>}
    </div>
  );
}

function NutritionBingo() {
  const allActions = [
    "Eat a fruit",
    "Drink water",
    "Walk 10 minutes",
    "Eat a veggie",
    "Skip sugary drink",
    "Try a new healthy food",
    "Cook at home",
    "Eat whole grains",
    "Stretch for 5 min",
    "Eat nuts",
    "Eat fish",
    "Eat yogurt",
    "Eat beans",
    "Eat berries",
    "Eat eggs",
  ];
  const [actions, setActions] = useState(() => shuffle(allActions).slice(0, 9));
  const [done, setDone] = useState(Array(9).fill(false));
  const handleClick = (idx) => {
    const newDone = [...done];
    newDone[idx] = true;
    setDone(newDone);
  };
  const handleReset = () => {
    setDone(Array(9).fill(false));
    setActions(shuffle(allActions).slice(0, 9));
  };
  const bingo = done.every(Boolean);
  return (
    <div>
      <p className="mb-2">Complete healthy actions to fill your bingo card:</p>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {actions.map((act, idx) => (
          <button
            key={act}
            className={`px-2 py-2 rounded border text-xs ${
              done[idx]
                ? "bg-green-300 border-green-600 text-white"
                : "bg-white border-blue-200 text-blue-700"
            }`}
            onClick={() => handleClick(idx)}
            disabled={done[idx]}
          >
            {act}
          </button>
        ))}
      </div>
      {bingo && (
        <p className="font-semibold text-yellow-600">
          🎉 Bingo! You completed all actions!
        </p>
      )}
      <button
        className="bg-gray-300 px-4 py-2 rounded mt-2"
        onClick={handleReset}
      >
        Reset
      </button>
    </div>
  );
}

export default GameSection;
